import type { BaseSyntheticEvent } from 'react';
import { useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApprovalScope } from './useApprovalScope';
import { fundingApproverSchema, type FundingApproverValues } from '../schemas/fundingApprover.schema';

export interface FundingApproverForm {
    control: Control<FundingApproverValues>;
    isSubmitting: boolean;
    locked: boolean;
    submit: (event?: BaseSyntheticEvent) => Promise<void>;
}

/** The submission lock and the server's per-address rejection belong to the scope that
 *  started the submit, so a request from before a reset neither blocks nor marks the next one. */
export function useFundingApproverForm(
    request: (approverEmail: string) => Promise<string | null>,
    busy = false,
    scopeKey: string | number = 0,
): FundingApproverForm {
    const { control, handleSubmit, setError } = useForm<FundingApproverValues>({
        resolver: zodResolver(fundingApproverSchema),
        defaultValues: { approverEmail: '' },
    });
    const scope = useApprovalScope(String(scopeKey));

    const submit = async (event?: BaseSyntheticEvent) => {
        event?.preventDefault();
        if (busy) return;
        const started = scope.token;
        if (!scope.lock.acquire(started)) return;
        try {
            await handleSubmit(async (values) => {
                const fieldMessage = await request(values.approverEmail);
                if (fieldMessage && scope.isCurrent(started)) setError('approverEmail', { message: fieldMessage });
            })(event);
        } finally {
            scope.lock.release(started);
        }
    };

    const isSubmitting = scope.lock.locked;
    return { control, isSubmitting, locked: busy || isSubmitting, submit };
}
